import { IdentityKind } from '@canvas/model/identity';

import { type BackgroundColorToken } from '@canvas/constants/colors/tokens-types';

import { type FieldEntryGrouping, type Participant } from './types.ts';
import { getGrouping, getKey } from './utils.ts';

/**
 * The participants present on one grouping, reduced to what the drawable
 * presence layer needs to render. `fieldId` / `entryId` are `null` when the
 * grouping targets an entire column / row respectively.
 */
export interface PresenceLayerGroupSnapshot {
  fieldId: string | null;
  entryId: string | null;
  participants: Array<{
    colorToken: BackgroundColorToken;
    displayName: string | undefined;
  }>;
}

/**
 * The single, shared presence store. It holds every collaborator's current
 * field/entry/cell grouping and is a *pure* store: it only emits which grouping
 * changed via {@link subscribe}. It does not itself talk to the ping
 * registry or the highlights layer — those are separate subscribers that branch
 * off this store (see `startPresenceComponentPings` for the per-component ping
 * bridge; the drawable renderer is the other subscriber).
 */
export class PresenceState {
  /**
   * Participants keyed by "<fieldId>,<entryId>"; either id may be absent,
   * meaning an entire column/row is targeted.
   */
  #participants: Map<string, Participant[]> = new Map();

  /**
   * Key is "<accountId>", value is the "<fieldId>-<entryId>" hash.
   */
  #participantsByAccountId: Map<string, string> = new Map();

  #listeners = new Set<(grouping: FieldEntryGrouping) => void>();

  addParticipant(grouping: FieldEntryGrouping, participant: Participant): void {
    // Move the participant off any previous grouping first, then add to the new
    // one. A move therefore emits both groupings.
    this.removeParticipant(participant);

    const key = getKey(grouping);
    const existing = this.#participants.get(key);

    if (existing) {
      this.#participants.set(key, [participant, ...existing]);
    } else {
      this.#participants.set(key, [participant]);
    }

    this.#participantsByAccountId.set(participant.identity.accountId, key);
    this.#emit(getGrouping(key));
  }

  removeParticipant(participant: Participant): void {
    const key = this.#participantsByAccountId.get(participant.identity.accountId);

    if (!key) {
      return;
    }

    const remaining = this.#participants
      .get(key)
      ?.filter((p) => p.identity.accountId !== participant.identity.accountId);

    if (remaining && remaining.length > 0) {
      this.#participants.set(key, remaining);
    } else {
      this.#participants.delete(key);
    }

    this.#participantsByAccountId.delete(participant.identity.accountId);
    this.#emit(getGrouping(key));
  }

  getParticipantsFromGrouping(grouping: FieldEntryGrouping): Participant[] {
    const key = getKey(grouping);

    return this.#participants.get(key) ?? [];
  }

  get groupingToParticipantsMap(): Map<string, Participant[]> {
    return this.#participants;
  }

  get accountIdToParticipantsMap(): ReadonlyMap<string, string> {
    return this.#participantsByAccountId;
  }

  getRecentCollaboratorColor(grouping: FieldEntryGrouping): string | undefined {
    const participants = this.getParticipantsFromGrouping(grouping);

    return participants.length > 0 ? participants[0].colorToken : undefined;
  }

  /**
   * Subscribe to store changes. The listener receives the grouping that changed
   * on each mutation (a move emits the old grouping, then the new one).
   * Subscribers branch this into their own concern — pinging the affected
   * component, or redrawing the drawable presence layer.
   */
  subscribe(listener: (grouping: FieldEntryGrouping) => void): () => void {
    this.#listeners.add(listener);

    return () => {
      this.#listeners.delete(listener);
    };
  }

  getGroups(): PresenceLayerGroupSnapshot[] {
    return this.#getGroups(false);
  }

  /**
   * Drawable human presence excludes AI before choosing the group's colour or
   * nameplate. AI streaming continues through its existing component path.
   */
  getHumanGroups(): PresenceLayerGroupSnapshot[] {
    return this.#getGroups(true);
  }

  #getGroups(humansOnly: boolean): PresenceLayerGroupSnapshot[] {
    const groups: PresenceLayerGroupSnapshot[] = [];

    for (const [key, participants] of this.#participants) {
      const visibleParticipants = humansOnly
        ? participants.filter(({ identity }) => identity.type !== IdentityKind.AiAgent)
        : participants;
      if (visibleParticipants.length === 0) {
        continue;
      }
      const grouping = getGrouping(key);

      groups.push({
        fieldId: 'fieldId' in grouping ? grouping.fieldId : null,
        entryId: 'entryId' in grouping ? grouping.entryId : null,
        participants: visibleParticipants.map((participant) => ({
          colorToken: participant.colorToken,
          displayName: participant.identity.displayName
        }))
      });
    }

    return groups;
  }

  #emit(grouping: FieldEntryGrouping): void {
    for (const listener of this.#listeners) {
      listener(grouping);
    }
  }
}
